/**
 * Janko Piano - Minimap Module
 * Handles minimap rendering, navigation, and pinch-to-zoom
 * @version 0.2.63
 */

import { CONFIG, MINIMAP_TOTAL_COLS, DIAMOND_PARAMS } from './config.js';
import { getNoteFromGrid, getCanvas, metrics, lastCanvasCssW } from './renderer.js';
import { setWidth, setScroll, minimapDirty, setMinimapDirty } from './ui.js';

// =============================================================================
// MINIMAP STATE
// =============================================================================

let miniCont = null;
let isDragMini = false;

// Throttle minimap updates for performance
let lastMinimapUpdate = 0;
let minimapInitialized = false;
const MINIMAP_THROTTLE_MS = 50; // ~20fps for minimap thumb updates

// =============================================================================
// INITIALIZATION
// =============================================================================

export function initMinimap(updateMinimapFn) {
    miniCont = document.getElementById('minimap-container');
    if (!miniCont) return;

    let initialPinchDist = null;
    let initialZoom = 1.0;

    miniCont.addEventListener('mousedown', e => {
        isDragMini = true;
        handleMinimapInput(e, updateMinimapFn);
    });
    
    window.addEventListener('mousemove', e => {
        if (isDragMini) {
            e.preventDefault();
            handleMinimapInput(e, updateMinimapFn);
        }
    });
    
    window.addEventListener('mouseup', () => {
        isDragMini = false;
    });

    miniCont.addEventListener('touchstart', e => {
        if (e.touches.length === 2) {
            isDragMini = false;
            e.preventDefault();
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            initialPinchDist = Math.sqrt(dx * dx + dy * dy);
            initialZoom = CONFIG.zoom;
        } else {
            isDragMini = true;
            handleMinimapInput(e, updateMinimapFn);
        }
    }, { passive: false });

    window.addEventListener('touchmove', e => {
        if (e.touches.length === 2 && initialPinchDist) {
            e.preventDefault();
            const dx = e.touches[0].clientX - e.touches[1].clientX;
            const dy = e.touches[0].clientY - e.touches[1].clientY;
            const dist = Math.sqrt(dx * dx + dy * dy);

            const ratio = dist / initialPinchDist;
            let newZoom = initialZoom / ratio;

            if (newZoom < 0.5) newZoom = 0.5;
            if (newZoom > 2.0) newZoom = 2.0;

            setWidth(newZoom, updateMinimapFn);
            const sl = document.getElementById('zoom-slider');
            if (sl) sl.value = newZoom;

        } else if (isDragMini) {
            e.preventDefault();
            handleMinimapInput(e, updateMinimapFn);
        }
    }, { passive: false });

    window.addEventListener('touchend', (e) => {
        if (e.touches.length < 2) initialPinchDist = null;
        if (e.touches.length === 0) isDragMini = false;
    });
}

// =============================================================================
// MINIMAP INPUT HANDLING
// =============================================================================

function handleMinimapInput(e, updateMinimapFn) {
    if (!miniCont) return;
    
    const r = miniCont.getBoundingClientRect();
    let cx = e.clientX;
    if (e.touches && e.touches.length > 0) cx = e.touches[0].clientX;
    let x = cx - r.left;
    if (x < 0) x = 0;
    if (x > r.width) x = r.width;

    const activeCols = CONFIG.activeTotalCols || MINIMAP_TOTAL_COLS;
    const thumb = document.getElementById('minimap-thumb');
    const thumbW = thumb.offsetWidth;
    const trackW = r.width;
    let targetLeftPx = x - (thumbW / 2);
    const maxLeftPx = Math.max(0, trackW - thumbW);
    if (targetLeftPx < 0) targetLeftPx = 0;
    if (targetLeftPx > maxLeftPx) targetLeftPx = maxLeftPx;
    // Use the stored CSS width from renderer (most reliable)
    const canvasCssW = lastCanvasCssW || 0;
    if (canvasCssW <= 0 || metrics.w <= 0) return;
    const viewCols = canvasCssW / metrics.w;
    const maxStartCol = Math.max(0, activeCols - viewCols);

    let leftPct = maxLeftPx > 0 ? (targetLeftPx / maxLeftPx) : 0;
    let targetStartCol = leftPct * maxStartCol;
    let newScroll = targetStartCol / 6;
    setScroll(newScroll, updateMinimapFn);
}

// =============================================================================
// ACTIVE COLUMNS CALCULATION
// =============================================================================

export function getActiveTotalCols() {
    if (!CONFIG.splitOctave) return MINIMAP_TOTAL_COLS;

    let leftBoundX = -Infinity;
    let rightBoundX = Infinity;

    for (let testR = 0; testR < CONFIG.rows; testR++) {
        const rowOffset = (testR % 2 !== 0) ? 0.5 : 0;
        const isEvenRow = (testR % 2 === 0);

        let minX = Infinity, maxX = -Infinity;
        for (let c = -10; c < 100; c++) {
            const n = getNoteFromGrid(testR, c);
            if (n.midi >= 21 && n.midi <= 104) {
                const x = c + rowOffset;
                if (x < minX) minX = x;
                if (x > maxX) maxX = x;
            }
        }

        if (isEvenRow && minX > leftBoundX && minX < Infinity) {
            leftBoundX = minX;
        }
        if (maxX < rightBoundX && maxX > -Infinity) {
            rightBoundX = maxX;
        }
    }

    return Math.floor(rightBoundX) - Math.floor(leftBoundX) + 1;
}

// =============================================================================
// MINIMAP KEY DRAWING
// =============================================================================

export function drawMinimapKeys() {
    const c = document.getElementById('minimap-canvas');
    if (!c) return;
    
    const ctxM = c.getContext('2d');
    const w = c.clientWidth;
    const h = c.clientHeight;
    if (c.width !== w || c.height !== h) {
        c.width = w;
        c.height = h;
    }

    ctxM.clearRect(0, 0, w, h);

    let totalCols = MINIMAP_TOTAL_COLS;
    let startC = 0;

    if (CONFIG.splitOctave) {
        let leftBoundCol = -Infinity;
        let rightBoundCol = Infinity;

        for (let testR = 0; testR < CONFIG.rows; testR++) {
            const isEvenRow = (testR % 2 === 0);

            let minCol = Infinity;
            let maxCol = -Infinity;
            for (let col = -10; col < 100; col++) {
                const n = getNoteFromGrid(testR, col);
                if (n.midi >= 21 && n.midi <= 104) {
                    if (col < minCol) minCol = col;
                    if (col > maxCol) maxCol = col;
                }
            }

            if (isEvenRow && minCol > leftBoundCol && minCol < Infinity) {
                leftBoundCol = minCol;
            }
            if (maxCol < rightBoundCol && maxCol > -Infinity) {
                rightBoundCol = maxCol;
            }
        }

        startC = leftBoundCol !== -Infinity ? leftBoundCol : 0;
        totalCols = rightBoundCol !== Infinity ? rightBoundCol - startC + 1 : MINIMAP_TOTAL_COLS;
    }

    CONFIG.activeTotalCols = totalCols;

    // Dynamic minimap rows based on orientation and settings
    const isPortrait = window.innerHeight > window.innerWidth;
    const isDesktop = window.innerWidth > 932;
    
    let minimapRows;
    if (isDesktop && !isPortrait && CONFIG.splitOctave && CONFIG.rows > 4) {
        // Desktop + landscape + split mode + rows > 4 = 1 row
        minimapRows = 1;
    } else if (!isPortrait || (CONFIG.splitOctave && CONFIG.rows > 8)) {
        // Landscape, or portrait with split mode + rows > 8 = 2 rows
        minimapRows = 2;
    } else {
        // Portrait = 4 rows
        minimapRows = 4;
    }
    
    const keyW = w / totalCols;
    const keyH = h / minimapRows;
    const shape = CONFIG.keyShape || 'original';

    const drawKey = (x, y, kw, kh) => {
        ctxM.beginPath();
        if (shape === 'circle') {
            ctxM.ellipse(x + kw / 2, y + kh / 2, kw / 2, kh / 2, 0, 0, 2 * Math.PI);
        } else if (shape === 'hexagon') {
            const cx = x + kw / 2, cy = y + kh / 2;
            const rx = kw / 2, ry = kh / 2;

            const v = [
                { x: cx + rx, y: cy + ry * 0.5 },
                { x: cx, y: cy + ry },
                { x: cx - rx, y: cy + ry * 0.5 },
                { x: cx - rx, y: cy - ry * 0.5 },
                { x: cx, y: cy - ry },
                { x: cx + rx, y: cy - ry * 0.5 }
            ];

            ctxM.moveTo(v[0].x, v[0].y);
            for (let i = 1; i < 6; i++) ctxM.lineTo(v[i].x, v[i].y);
            ctxM.lineTo(v[0].x, v[0].y);
        } else if (shape === 'diamond') {
            const cx = x + kw / 2, cy = y + kh / 2;
            const pt = { x: cx, y: y };
            const pr = { x: x + kw, y: cy };
            const pb = { x: cx, y: y + kh };
            const pl = { x: x, y: cy };
            const radV = DIAMOND_PARAMS.CORNER_V * 0.25;
            const radH = DIAMOND_PARAMS.CORNER_H * 0.25;

            ctxM.moveTo((pl.x + pt.x) / 2, (pl.y + pt.y) / 2);
            ctxM.arcTo(pt.x, pt.y, pr.x, pr.y, radV);
            ctxM.arcTo(pr.x, pr.y, pb.x, pb.y, radH);
            ctxM.arcTo(pb.x, pb.y, pl.x, pl.y, radV);
            ctxM.arcTo(pl.x, pl.y, pt.x, pt.y, radH);
        } else if (shape === 'square' || shape === 'original') {
            if (ctxM.roundRect) {
                ctxM.roundRect(x, y, kw - 1, kh - 1, 2);
            } else {
                ctxM.rect(x, y, kw, kh);
            }
        }
        ctxM.fill();
    };

    // Draw all rows dynamically
    for (let row = 0; row < minimapRows; row++) {
        const isOddRow = row % 2 === 1;
        const xOffset = isOddRow ? keyW / 2 : 0;
        const yPos = row * keyH;
        
        for (let i = 0; i < totalCols; i++) {
            const note = getNoteFromGrid(row % 2, startC + i);
            if (note.midi < 21 || note.midi > 104) continue;
            ctxM.fillStyle = note.isWhite ? '#e0e0e0' : '#1f1f1f';
            drawKey((i * keyW) + xOffset, yPos, keyW, keyH);
        }
    }
}

// =============================================================================
// MINIMAP UPDATE
// =============================================================================

export function updateMinimap(maxScroll, forceUpdate = false) {
    
    const thumb = document.getElementById('minimap-thumb');
    miniCont = document.getElementById('minimap-container');
    if (!thumb || !miniCont) return;

    const c = document.getElementById('minimap-canvas');
    if (c) {
        if (c.width !== c.clientWidth || c.height !== c.clientHeight) {
            c.width = c.clientWidth;
            c.height = c.clientHeight;
            setMinimapDirty(true);
        }

        if (minimapDirty) {
            drawMinimapKeys();
            setMinimapDirty(false);
        }
    }

    const activeCols = CONFIG.activeTotalCols || MINIMAP_TOTAL_COLS;
    // Use the stored CSS width from renderer (most reliable)
    const canvasCssW = lastCanvasCssW || 0;
    
    // Guard against metrics.w being 0 (not yet initialized)
    if (metrics.w <= 0 || canvasCssW <= 0) {
        return;
    }
    
    let viewCols = canvasCssW / metrics.w;
    let pctW = (viewCols / activeCols) * 100;
    if (pctW > 100) pctW = 100;
    thumb.style.width = pctW + "%";
    
    
    const slider = document.getElementById('scroll-slider');
    if (slider) {
        let correctMaxOff = activeCols - viewCols;
        if (correctMaxOff < 0) correctMaxOff = 0;
        slider.max = correctMaxOff / 6;
        if (CONFIG.scrollPos > slider.max) {
            CONFIG.scrollPos = slider.max;
            slider.value = slider.max;
        }
    }

    const trackW = miniCont.getBoundingClientRect().width;
    const thumbW = thumb.getBoundingClientRect().width;
    const maxLeftPx = Math.max(0, trackW - thumbW);

    const startCol = CONFIG.scrollPos * 6;
    const maxStartCol = Math.max(0, activeCols - viewCols);
    const frac = maxStartCol > 0 ? (startCol / maxStartCol) : 0;
    const leftPx = Math.min(maxLeftPx, Math.max(0, frac * maxLeftPx));

    thumb.style.left = '0px';
    thumb.style.transform = `translateX(${leftPx}px)`;
}
